import { useTranslation } from "react-i18next";

import { useAlert } from "@/hooks/useAlert";
import { useLocalStorageFlag } from "@/hooks/useLocalStorageFlag";
import { useGetCustomer, useGetESimInfo } from "@/services/api";
import { AlertTypes } from "@/utils/atoms";

/**
 * Loads the side data the device management view needs alongside the SIM: the
 * customer record (used to decide which tabs a customer may see) and the eSIM
 * state for the displayed SIM. A failure to load the eSIM state surfaces a
 * throttled info alert, mirroring the SIM query's behaviour.
 */
export const useManagedSimSideData = (simId: string) => {
  const showAlert = useAlert();
  const { t } = useTranslation("portal");
  const {
    shouldShowError: shouldShowEsimInfoError,
    setErrorFired: setEsimInfoErrorFired,
  } = useLocalStorageFlag("esimInfoErrorFiredAt");

  const { data: customerData } = useGetCustomer();

  const { data: esimInfo } = useGetESimInfo(
    { simIds: [parseInt(simId)] },
    {
      query: {
        enabled: !isNaN(parseInt(simId)),
        onError: () => {
          if (shouldShowEsimInfoError()) {
            showAlert({
              type: AlertTypes.info,
              text: t("Error.eSimError"),
            });
            setEsimInfoErrorFired();
          }
        },
      },
    },
  );

  return { customerData, esimInfo };
};
